import {AfterViewChecked, NgZone, ViewChild, ViewEncapsulation} from '@angular/core';
import {Component, OnInit} from '@angular/core';
import { of } from 'rxjs';
import {AuthenticationService} from "../authentication.service";
import { ActivatedRoute, Router } from '@angular/router';
import {EventService} from "../../core/services/event.service";
import {HttpClient} from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { PlatformLocation } from '@angular/common';
import {ProgressService} from "../../core/services/progress.service";
import { HybridToolsService } from '../../core/services/hybrid-tools.service';
import {SplashScreen} from '@capacitor/splash-screen';
import {UtilsService} from "../../core/services/utils.service";
import {FacebookService} from "../../core/services/facebook.service";
import {FacebookLoginResponse} from "@capacitor-community/facebook-login";
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';

declare var ENVIRONMENT_CONFIG: any;
declare var google: any;

@Component({
    selector: 'app-login',
    encapsulation: ViewEncapsulation.None,
    templateUrl: './login.page.html',
    styleUrls: ['./login.page.scss']
})

export class LoginPage implements OnInit, AfterViewChecked {
    @ViewChild('loginForm', { static: true }) loginForm;
    @ViewChild('resetPasswordForm', { static: true }) resetPasswordForm;
    @ViewChild('updatePasswordForm', { static: true }) updatePasswordForm;
    loginModel = {
        username: null,
        password: null,
        rememberMe: null
    };
    resetPasswordModel = {
        email: null
    };
    updatePasswordModel = {
        password: null,
        confirm: null
    };
    isErrorMessageShowing = false;
    errorMessage: string = null;

    splashDisplayed = true;

    googleLoaded = false;

    constructor(
        private authService: AuthenticationService,
        private router: Router,
        public eventService: EventService,
        private zone: NgZone,
        private http: HttpClient,
        private toastr: ToastrService,
        private progressService: ProgressService,
        private activatedRoute: ActivatedRoute,
        public hybridToolsService: HybridToolsService,
        location: PlatformLocation,
        private facebookService: FacebookService,
        private utilsService: UtilsService
    ) {
        location.onPopState(() => {
            this.backToLogin();
        });
    }

    ngOnInit() {
        // Don't prompt to log in if we already are logged in.
        if (this.authService.currentUser()) {
            this.router.navigate(['/after-login']);
        }

        if (this.activatedRoute.snapshot.queryParams.pwToken) {
            this.validatePasswordToken();
        }
    }

    ngAfterViewChecked() {
        this.hideSplashScreen();

        if (!this.hybridToolsService.isMobileApp()) {
            if (google && !this.googleLoaded) {
                this.googleLoaded = true;

                google.accounts.id.renderButton(
                    document.getElementById('googleSignInContainer'),
                    {
                        type: 'icon'
                    }
                );

                //assume google auth is loaded at this point, so we can also set the callback
                GoogleAuth.setSignInCallback(this.googleLoginInternal.bind(this));
            }
        }
    }

    hideSplashScreen() {
        if (this.splashDisplayed && this.hybridToolsService.isMobileApp()) {
            this.splashDisplayed = false;
            SplashScreen.hide();
        }
    }

    forgotPassword() {
        $('.cat__pages__login__block__form').hide();
        $('.cat__pages__reset__password__form').show();
        history.pushState(null, "", "/");
    }

    backToLogin(){
        $('.cat__pages__login__block__form').show();
        $('.cat__pages__reset__password__form').hide();
        $('.cat__pages__update__password__form').hide();
    }

    resetPassword() {
        if (this.resetPasswordForm.valid){
            this.http.post('/api/v1/auth/resetPassword', { "emailAddress" : this.resetPasswordModel.email })
            .subscribe(res => {
                this.backToLogin();
                this.toastr.success('Your password reset email has been sent to you.');
            }, err => {
                this.toastr.error("Error");
            });
        }
    }

    updatePassword() {
        if (this.updatePasswordForm.controls['Confirm'].valid) {
            if (this.updatePasswordModel.password != this.updatePasswordModel.confirm) {
                this.updatePasswordForm.controls['Confirm'].setErrors({'matchError': true});
            } else {
                this.updatePasswordForm.controls['Confirm'].setErrors(null);
            }
        }

        if (this.updatePasswordForm.valid) {
            this.http.post('/api/v1/auth/updatePassword', {
                "emailAddress": this.activatedRoute.snapshot.queryParams.emailAddress,
                "password": this.updatePasswordModel.password,
                "token": this.activatedRoute.snapshot.queryParams.pwToken,
                "callback": this.activatedRoute.snapshot.queryParams.callback
            })
            .subscribe(res => {
                var callbackUrl = this.activatedRoute.snapshot.queryParams.callback;
                if (callbackUrl != null) {
                    window.location.assign(callbackUrl);
                } else {
                    this.backToLogin();
                }
                this.toastr.success('Your password has been updated.');
            }, err => {
                let errMsg = "Error";
                if (err && err.status == 406) {
                    errMsg = "Invalid password"
                }
                this.toastr.error(`${errMsg}`);
            });
        }
    }

    validatePasswordToken() {
        this.http.put('/api/v1/auth/updatePassword', {
            "emailAddress": this.activatedRoute.snapshot.queryParams.emailAddress,
            "token": this.activatedRoute.snapshot.queryParams.pwToken,
            "callback": this.activatedRoute.snapshot.queryParams.callback
        })
        .subscribe(res => {
        }, err => {
            this.toastr.error("Your reset link has expired!");
            this.backToLogin();
        });
    }

    socialLogin(provider) {
        //check if the user is on IE and then display a message for them
        if (this.utilsService.isIE()){
            $('.ie-modal')
                .show()
                .find('[data-dismiss="modal"]').click(function(){ $(this).closest('.ie-modal').hide(); });
            return;
        }

        if (provider == 'facebook') {
            this.facebookLogin();
        } else {
            this.googleLogin();
        }
    }

    async facebookLogin() {

        const result: FacebookLoginResponse = await this.facebookService.getAccessToken();

        // logout will fail on web if you don't have a token
        if (result?.accessToken?.token) {
            //necessary for iOS/Android apps
            await this.facebookService.logout();
        }

        if(await this.facebookService.login()) {
            const profile = await this.facebookService.getProfile();
            const result = await this.facebookService.getAccessToken();
            if (result) {
                profile.accessToken = result.accessToken;
            }
            profile.provider = 'facebook';
            this.handleSocialResponse(profile);
        } else {
            this.toastr.error("Error Logging Into Facebook");
        }
    }

    async googleLogin() {
        let googleUser = await GoogleAuth.signIn();
        this.googleLoginInternal(googleUser);
    }

    googleLoginInternal(googleUser) {
        let profile: any = {};
        if (googleUser) {
            profile.provider = 'google';
            profile.user = googleUser;
            this.handleSocialResponse(profile);
        } else {
            this.toastr.error("Error Logging Into Google");
        }
    }

    onSubmit() {
        if (this.loginForm.valid) {
            this.progressService.startProgress(false);
            this.authService.login(this.loginModel.username, this.loginModel.password, this.loginModel.rememberMe)
                .subscribe(
                    response => {
                        this.router.navigate(['/after-login']);
                        this.progressService.stopProgress(false);
                    },
                    response => {
                        this.errorMessage = JSON.parse(response.error).message;
                        // Give failed login a better error message.
                        if (this.errorMessage == "Authentication Failed: Bad credentials") {
                            this.errorMessage = "That login does not match our records. Please try again.";
                        }
                        this.progressService.stopProgress(false);
                    });
        }
    }

    private handleSocialResponse(res) {
        this.progressService.startProgress(false);
        this.authService.socialLogin(res).subscribe(response => {
            this.zone.run(() => {
                this.router.navigate(['/after-login']);
            });
            this.progressService.stopProgress(false);
        }, err => {
            this.zone.run(() => {
                this.errorMessage = 'You have not registered for any events. ' +
                    'You must register for an event before you can use this service';
            });
            this.progressService.stopProgress(false);
        });
    }

    get environment() {
        let result;
        let apiUrl = ENVIRONMENT_CONFIG.apiUrl;

        if (apiUrl && apiUrl.indexOf('://fundraise.lls.org') === -1 && apiUrl.indexOf('fundraise.lls.org') !== -1) {
            result = apiUrl.substring(apiUrl.indexOf("://") + 3, apiUrl.indexOf("fundraise.lls.org"));
        }

        return result;
    }

    get fundraiseUrl() {
        return ENVIRONMENT_CONFIG.fundraiseUrl;
    }
}
